import { useEffect, useMemo, useState } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { toIntlLocale } from "@/lib/app-language"
import type {
  ThirdPartyLicensesManifest,
  ThirdPartyPackage,
} from "@/lib/third-party-licenses-types"

const safeHttpUrl = (value: string | undefined) => {
  if (!value) {
    return undefined
  }
  try {
    const parsed = new URL(value)
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.href
    }
  } catch {
    return undefined
  }
  return undefined
}

const formatGeneratedAt = (iso: string, locale: string) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

const PackageRow = ({ pkg }: { pkg: ThirdPartyPackage }) => {
  const { t } = useTranslation()
  const label = pkg.version ? `${pkg.name}@${pkg.version}` : pkg.name
  const repositoryHref = safeHttpUrl(pkg.repository)

  return (
    <details className="group rounded-lg border border-border/80 bg-card px-4 py-3">
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium">{label}</span>
          <Badge variant="secondary">{pkg.licenses}</Badge>
          {repositoryHref && (
            <a
              href={repositoryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline-offset-4 hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {t("licenses.repository")}
            </a>
          )}
        </div>
      </summary>
      {pkg.licenseText ? (
        <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 font-mono text-xs leading-relaxed text-muted-foreground">
          {pkg.licenseText}
        </pre>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {t("licenses.licenseTextUnavailable")}
        </p>
      )}
    </details>
  )
}

function LicensesPage() {
  const { t, i18n } = useTranslation()
  const [query, setQuery] = useState("")
  const [data, setData] = useState<ThirdPartyLicensesManifest | null>(null)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    let cancelled = false
    void import("@/generated/third-party-licenses.json")
      .then((manifest) => {
        if (!cancelled) {
          setData(manifest.default as ThirdPartyLicensesManifest)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const packages = useMemo(() => {
    if (!data) {
      return []
    }
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) {
      return data.packages
    }
    return data.packages.filter((pkg) => {
      const haystack = [
        pkg.name,
        pkg.version,
        pkg.licenses,
        pkg.repository ?? "",
      ]
        .join(" ")
        .toLowerCase()
      return haystack.includes(normalizedQuery)
    })
  }, [data, query])

  if (loadError) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 py-6">
        <p className="text-sm text-destructive">{t("licenses.loadError")}</p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/">{t("licenses.backToHome")}</Link>
        </Button>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 py-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-4 w-full max-w-2xl" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="text-foreground">
      <header className="mb-8 border-b border-border/80 pb-4">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <p className="text-2xl font-semibold">
            <Link to="/">
              <span className="text-green-600">myLangguth</span> Shop
            </Link>
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to="/">{t("licenses.backToHome")}</Link>
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">
          {t("licenses.title")}
        </h1>
        <p className="max-w-3xl text-muted-foreground">
          {t("licenses.description")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("licenses.lastUpdated", {
            date: formatGeneratedAt(
              data.generatedAt,
              toIntlLocale(i18n.language),
            ),
            count: data.packages.length,
          })}
        </p>
      </div>

      <div className="mx-auto mt-8 max-w-5xl">
        <label htmlFor="license-search" className="sr-only">
          {t("licenses.searchLabel")}
        </label>
        <Input
          id="license-search"
          type="search"
          placeholder={t("licenses.searchPlaceholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="max-w-xl"
        />
      </div>

      <div className="mx-auto mt-8 max-w-5xl space-y-3">
        {packages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("licenses.noResults")}
          </p>
        ) : (
          packages.map((pkg) => (
            <PackageRow key={`${pkg.name}@${pkg.version}`} pkg={pkg} />
          ))
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute("/legal/licenses")({
  component: LicensesPage,
})
